/**
 * Footer Component (Material UI)
 * Clean, professional footer following MUI marketing page template style
 */
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Link,
  Stack,
  Divider,
} from '@mui/material';
import { MonetizationOn as MonetizationOnIcon } from '@mui/icons-material';
import { useCustomTranslation } from '../../hooks';

export default function FooterMUI() {
  const { t } = useCustomTranslation();

  const footerLinks = [
    {
      title: t('footer.product'),
      links: [
        { label: t('footer.calculators'), path: '/' },
        { label: t('footer.blog'), path: '/blog' },
      ],
    },
    {
      title: t('footer.company'),
      links: [
        { label: t('footer.about'), path: '/about' },
        { label: t('footer.contact'), path: '/contact' },
      ],
    },
    {
      title: t('footer.legal'),
      links: [
        { label: t('footer.privacy'), path: '/privacy' },
        { label: t('footer.terms'), path: '/terms' },
      ],
    },
  ];

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
        py: { xs: 6, md: 8 },
        mt: 'auto',
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 4,
            mb: 6,
          }}
        >
          {/* Brand Section */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <MonetizationOnIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                PayWise365
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('footer.description')}
            </Typography>
          </Box>

          {/* Footer Links */}
          {footerLinks.map((section, index) => (
            <Box key={index}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                {section.title}
              </Typography>
              <Stack spacing={1.5}>
                {section.links.map((link, linkIndex) => (
                  <Link
                    key={linkIndex}
                    component={RouterLink}
                    to={link.path}
                    underline="none"
                    color="text.secondary"
                    sx={{
                      transition: 'color 0.2s',
                      '&:hover': {
                        color: 'primary.main',
                      },
                    }}
                  >
                    <Typography variant="body2">{link.label}</Typography>
                  </Link>
                ))}
              </Stack>
            </Box>
          ))}
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Affiliate Disclosure */}
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ display: 'block', textAlign: 'center', mb: 3, lineHeight: 1.6, maxWidth: 640, mx: 'auto' }}
        >
          Some links on this site are affiliate links. PayWise365 may earn a commission if you sign up or make a purchase through these links, at no extra cost to you. As an Amazon Associate, PayWise365 earns from qualifying purchases.
        </Typography>

        <Divider sx={{ mb: 4 }} />

        {/* Bottom Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} PayWise365. {t('footer.rights')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('footer.contact')}: paywise365@gmail.com
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
